import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { formsApi } from '../api/api';
import { Form, FormSubmission } from '../types/form';
import { ArrowLeft, Download, Calendar, User, Eye, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

const FormSubmissions: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<Form | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [formResponse, submissionsResponse] = await Promise.all([
        formsApi.getForm(id!),
        formsApi.getFormSubmissions(id!)
      ]);
      setForm(formResponse.data);
      
      // Ensure submissions is always an array
      const submissionsData = submissionsResponse.data;
      let newSubmissions: FormSubmission[] = [];
      
      if (Array.isArray(submissionsData)) {
        newSubmissions = submissionsData;
      } else if (submissionsData && typeof submissionsData === 'object' && 'results' in submissionsData && Array.isArray((submissionsData as any).results)) {
        // Handle paginated response
        newSubmissions = (submissionsData as any).results;
      } else {
        console.error('Unexpected submissions API response format:', submissionsData);
        newSubmissions = [];
      }
      
      setSubmissions(newSubmissions);
      
      // Update selectedSubmission to point to the new data if one was previously selected
      setSelectedSubmission(prev => {
        if (prev) {
          const updatedSubmission = newSubmissions.find(s => s.id === prev.id);
          return updatedSubmission || null;
        }
        return null;
      });
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setSubmissions([]); // Set empty array on error
      setSelectedSubmission(null); // Clear selected submission on error
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  const exportToPDF = async (submissionId?: string) => {
    if (!form) return;

    try {
      const response = await formsApi.exportSubmissionsPdf(id!, submissionId);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      const filename = submissionId 
        ? `${form.title.replace(' ', '_')}_submission_${submissionId.slice(-8)}.pdf`
        : `${form.title.replace(' ', '_')}_all_submissions.pdf`;
      a.download = filename;
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
    } catch (error: any) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF. Please try again later.');
    }
  };

  const exportToCSV = () => {
    if (!form || submissions.length === 0) return;

    // Get all unique field labels from historical submissions (preserves field order from submissions)
    const allFieldLabels = new Set<string>();
    submissions.forEach(submission => {
      submission.values.forEach(value => {
        allFieldLabels.add(value.field_label);
      });
    });
    
    const headers = ['Submission Date', 'IP Address', ...Array.from(allFieldLabels)];
    const rows = submissions.map(submission => {
      const row = [
        new Date(submission.submitted_at).toLocaleString(),
        submission.ip_address || 'N/A'
      ];
      
      // Add values for each historical field label
      Array.from(allFieldLabels).forEach(fieldLabel => {
        const submissionValue = submission.values.find(v => v.field_label === fieldLabel);
        const value = submissionValue ? formatValue(submissionValue, submissionValue.value) : '';
        row.push(value);
      });
      
      return row;
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form.title}-submissions.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resolveOptionValue = (submissionValue: any, value: any) => {
    if (!value) return '';
    
    // For multi-row fields, format as a table
    if (submissionValue.field_type === 'multi_row' && Array.isArray(value)) {
      return value.map((row: any, index: number) => {
        if (Array.isArray(row)) {
          // Handle 2D array format [["val1", "val2"], ["val3", "val4"]]
          const columns = submissionValue.field_options?.columns || [];
          const rowEntries = row.map((cellValue, colIndex) => {
            const columnLabel = columns[colIndex]?.label || `Column ${colIndex + 1}`;
            return `${columnLabel}: ${cellValue}`;
          }).join(', ');
          return `Row ${index + 1}: ${rowEntries}`;
        } else {
          // Handle object format [{key: "val1"}, {key: "val2"}]
          const rowEntries = Object.entries(row).map(([key, val]) => `${key}: ${val}`).join(', ');
          return `Row ${index + 1}: ${rowEntries}`;
        }
      }).join('\n');
    }
    
    // For array values (multiple selections)
    if (Array.isArray(value)) {
      return value.map(val => {
        if (typeof val === 'string' && val.startsWith('option') && submissionValue.field_options) {
          // Resolve option key to actual value
          return submissionValue.field_options[val] || val;
        }
        return val;
      }).join(', ');
    }
    
    // For single values that are option keys
    if (typeof value === 'string' && value.startsWith('option') && submissionValue.field_options) {
      // Resolve option key to actual value
      return submissionValue.field_options[value] || value;
    }
    
    // Handle file/image upload fields (for CSV export)
    if (typeof value === 'object' && value !== null && 'file_url' in value) {
      const fileData = value as { file_url: string; file_name: string; file_size: number };
      return `${fileData.file_name} - ${fileData.file_url}`;
    }

    // For other objects, stringify
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }

    return String(value);
  };

  const formatValue = (submissionValue: any, value: any) => {
    return resolveOptionValue(submissionValue, value);
  };

  const renderValue = (submissionValue: any, value: any) => {
    if (!value) return '';

    // A file that failed validation (malicious / disallowed type) — the rest of
    // the submission was still accepted; the file itself was dropped.
    if (typeof value === 'object' && value !== null && value.file_rejected) {
      const rejected = value as { file_name?: string; reason?: string };
      return (
        <span className="inline-flex items-center gap-1 text-destructive" title={rejected.reason || ''}>
          ⚠️ File rejected{rejected.file_name ? `: ${rejected.file_name}` : ''}
          {rejected.reason ? ` (${rejected.reason})` : ''}
        </span>
      );
    }

    // Handle file/image upload fields
    if (typeof value === 'object' && value !== null && 'file_url' in value) {
      const fileData = value as { file_url: string; file_name: string; file_size: number; content_type: string };

      // Check if it's an image
      if (fileData.content_type?.startsWith('image/')) {
        return (
          <div className="space-y-2">
            <img
              src={fileData.file_url}
              alt={fileData.file_name}
              className="max-w-xs max-h-48 rounded border"
            />
            <p className="text-xs text-muted-foreground">{fileData.file_name} ({(fileData.file_size / 1024 / 1024).toFixed(2)} MB)</p>
          </div>
        );
      } else {
        // For non-image files, show download link
        return (
          <a
            href={fileData.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            📎 {fileData.file_name} ({(fileData.file_size / 1024 / 1024).toFixed(2)} MB)
          </a>
        );
      }
    }

    // For all other values, use the formatValue function
    return formatValue(submissionValue, value);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Form Not Found</h2>
          <p className="text-muted-foreground">The form you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-4">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{form.title}</h1>
            <p className="text-muted-foreground">Form Submissions</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {submissions.length > 0 && (
            <>
              <Button variant="outline" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" onClick={() => exportToPDF()}>
                <FileText className="w-4 h-4 mr-2" />
                Export All PDF
              </Button>
            </>
          )}
          {form.is_published ? (
            <Link to={`/public/${id}`} target="_blank">
              <Button variant="outline">
                View Public Form
              </Button>
            </Link>
          ) : (
            <Button 
              variant="outline" 
              disabled
              title="Form must be published to view publicly"
            >
              View Public Form
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Submissions</p>
                <p className="text-2xl font-bold">{submissions.length}</p>
              </div>
              <User className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Form Fields</p>
                <p className="text-2xl font-bold">{form.fields.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <p className="text-lg font-semibold">
                  {form.is_published ? (
                    <span className="text-green-600">Published</span>
                  ) : (
                    <span className="text-muted-foreground">Draft</span>
                  )}
                </p>
              </div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                form.is_published ? 'bg-green-100' : 'bg-muted'
              }`}>
                <div className={`w-3 h-3 rounded-full ${
                  form.is_published ? 'bg-green-500' : 'bg-muted-foreground'
                }`}></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {submissions.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
              <User className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No submissions yet</h3>
            <p className="text-muted-foreground mb-4">
              {form.is_published 
                ? "Share your form link to start receiving submissions"
                : "Publish your form to start receiving submissions"
              }
            </p>
            {!form.is_published && (
              <Link to={`/forms/${id}/edit`}>
                <Button>Edit & Publish Form</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Submissions List */}
          <Card>
            <CardHeader>
              <CardTitle>Submissions</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-96 overflow-y-auto">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    className={`p-4 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedSubmission?.id === submission.id ? 'bg-muted' : ''
                    }`}
                    onClick={() => setSelectedSubmission(submission)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-foreground">
                          Submission #{submission.id.slice(-8)}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(submission.submitted_at).toLocaleString()}
                        </p>
                        {submission.one_use_link && (
                          <p className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full inline-block mt-1">
                            Via: {submission.one_use_link.name}
                          </p>
                        )}
                        {submission.ip_address && (
                          <p className="text-xs text-muted-foreground">
                            IP: {submission.ip_address}
                          </p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submission Detail */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                {selectedSubmission ? `Submission Details` : 'Select a Submission'}
              </CardTitle>
              <div className="flex items-center space-x-2">
                {selectedSubmission && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToPDF(selectedSubmission.id)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                )}
                {selectedSubmission && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedSubmission(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedSubmission ? (
                <div className="space-y-4">
                  <div className="pb-4 border-b border-border">
                    <h4 className="font-medium text-foreground">
                      Submission #{selectedSubmission.id.slice(-8)}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Submitted: {new Date(selectedSubmission.submitted_at).toLocaleString()}
                    </p>
                    {selectedSubmission.one_use_link && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Submitted via: </span>
                        <span className="text-primary bg-primary/10 px-2 py-1 rounded-full text-xs">
                          {selectedSubmission.one_use_link.name}
                        </span>
                      </div>
                    )}
                    {selectedSubmission.ip_address && (
                      <p className="text-sm text-muted-foreground">
                        IP Address: {selectedSubmission.ip_address}
                      </p>
                    )}
                  </div>
                  
                  {/* Historical Form Structure */}
                  <div className="space-y-4">
                    <h5 className="font-medium text-foreground">Form Data <span className="text-xs text-muted-foreground">(as submitted on {new Date(selectedSubmission.submitted_at).toLocaleDateString()})</span></h5>
                    {(() => {
                      // Sort submission values by the form's field order
                      const sortedValues = [...selectedSubmission.values].sort((a, b) => {
                        const fieldA = form.fields.find(f => f.id === a.field);
                        const fieldB = form.fields.find(f => f.id === b.field);
                        const orderA = fieldA?.order ?? 999;
                        const orderB = fieldB?.order ?? 999;
                        return orderA - orderB;
                      });

                      return sortedValues.map((submissionValue) => {
                        const value = submissionValue ? renderValue(submissionValue, submissionValue.value) : '';

                        return (
                        <div key={submissionValue.field} className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            {submissionValue.field_label}
                            {submissionValue.field_deleted && <span className="text-red-500 ml-1 text-xs">(Field Deleted)</span>}
                          </label>
                          
                          {submissionValue && submissionValue.field_type === 'multi_row' ? (
                            <div className="border border-border rounded-md bg-muted">
                              {(() => {
                                // Check if value exists and is an array with content
                                if (submissionValue.value && Array.isArray(submissionValue.value) && submissionValue.value.length > 0) {
                                  // Filter out empty rows
                                  const multiRowValue = submissionValue.value as any[];
                                  const nonEmptyRows = multiRowValue.filter((row: any) => {
                                    if (Array.isArray(row)) {
                                      return row.some(cell => cell !== '' && cell !== null && cell !== undefined);
                                    } else {
                                      return Object.values(row).some(val => val !== '' && val !== null && val !== undefined);
                                    }
                                  });
                                  
                                  if (nonEmptyRows.length === 0) {
                                    return (
                                      <div className="text-muted-foreground text-sm p-3">No data provided</div>
                                    );
                                  }
                                  
                                  // Get column configuration
                                  const columns = submissionValue.field_options?.columns || [];
                                  
                                  return (
                                    <div className="overflow-x-auto">
                                      <table className="w-full">
                                        {/* Table Header */}
                                        <thead>
                                          <tr className="border-b border-border">
                                            <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-2 bg-muted/50">
                                              Row
                                            </th>
                                            {Array.isArray(nonEmptyRows[0]) ? (
                                              // Handle 2D array format - use column labels
                                              columns.map((column: any, colIndex: number) => (
                                                <th key={colIndex} className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-2 bg-muted/50">
                                                  {column.label || `Column ${colIndex + 1}`}
                                                </th>
                                              ))
                                            ) : (
                                              // Handle object format - use object keys
                                              Object.keys(nonEmptyRows[0]).map((key) => (
                                                <th key={key} className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider p-2 bg-muted/50">
                                                  {key}
                                                </th>
                                              ))
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                          {nonEmptyRows.map((row: any, rowIndex: number) => (
                                            <tr key={rowIndex}>
                                              <td className="p-2 text-sm font-medium text-foreground">
                                                {rowIndex + 1}
                                              </td>
                                              {Array.isArray(row) ? (
                                                // Handle 2D array format
                                                row.map((cellValue, colIndex) => (
                                                  <td key={colIndex} className="p-2 text-sm text-foreground">
                                                    {String(cellValue) || '-'}
                                                  </td>
                                                ))
                                              ) : (
                                                // Handle object format
                                                Object.values(row).map((val, colIndex) => (
                                                  <td key={colIndex} className="p-2 text-sm text-foreground">
                                                    {String(val) || '-'}
                                                  </td>
                                                ))
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="text-muted-foreground text-sm p-3">
                                      No data provided
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          ) : submissionValue && submissionValue.field_type === 'textarea' ? (
                            <textarea
                              className="w-full px-3 py-2 border border-border rounded-md bg-muted text-foreground"
                              rows={3}
                              value={value}
                              disabled
                              readOnly
                            />
                          ) : submissionValue && submissionValue.field_type === 'dropdown' ? (
                            <select
                              className="w-full px-3 py-2 border border-border rounded-md bg-muted text-foreground"
                              value={value}
                              disabled
                            >
                              <option value={value}>{value || 'No selection'}</option>
                            </select>
                          ) : submissionValue && submissionValue.field_type === 'checkbox' ? (
                            <div className="space-y-2 border border-border rounded-md p-3 bg-muted">
                              {Object.entries(submissionValue.field_options || {}).map(([key, optionValue]) => {
                                const selectedValues: any[] = Array.isArray(submissionValue.value) ? submissionValue.value : (submissionValue.value ? [String(submissionValue.value)] : []);
                                const isChecked = selectedValues.includes(key) || selectedValues.includes(String(optionValue));
                                return (
                                  <div key={key} className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <label className="text-sm text-foreground">{optionValue as string}</label>
                                  </div>
                                );
                              })}
                            </div>
                          ) : submissionValue && submissionValue.field_type === 'checkbox_with_input' ? (
                            <div className="space-y-3 border border-border rounded-md p-3 bg-muted">
                              {(() => {
                                const checkboxData: { [key: string]: { checked: boolean; value: string } } = 
                                  (typeof submissionValue.value === 'object' && submissionValue.value !== null && !Array.isArray(submissionValue.value)) 
                                    ? submissionValue.value as { [key: string]: { checked: boolean; value: string } }
                                    : {};
                                return Object.entries(submissionValue.field_options || {})
                                  .filter(([key]) => key !== 'inputType' && key !== 'inputPlaceholder')
                                  .map(([key, optionValue]) => {
                                    const optionData = checkboxData[key] || { checked: false, value: '' };
                                    const isChecked = optionData.checked;
                                    const inputValue = optionData.value || '';
                                    
                                    return (
                                      <div key={key} className="flex items-center space-x-3">
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          disabled
                                          className="w-4 h-4 text-blue-600"
                                        />
                                        <label className="text-sm text-foreground min-w-0">{optionValue as string}</label>
                                        <div className="flex-1">
                                          <Input
                                            type={submissionValue.field_options?.inputType || 'text'}
                                            value={inputValue}
                                            disabled
                                            className="bg-background text-foreground"
                                            placeholder={submissionValue.field_options?.inputPlaceholder || 'Enter value'}
                                          />
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          ) : submissionValue && submissionValue.field_type === 'radio' ? (
                            <div className="space-y-2 border border-border rounded-md p-3 bg-muted">
                              {Object.entries(submissionValue.field_options || {}).map(([key, optionValue]) => {
                                const isSelected = submissionValue.value === key || submissionValue.value === optionValue;
                                return (
                                  <div key={key} className="flex items-center space-x-2">
                                    <input
                                      type="radio"
                                      name={`submission-${submissionValue.field}`}
                                      checked={isSelected}
                                      disabled
                                      className="w-4 h-4 text-blue-600"
                                    />
                                    <label className="text-sm text-foreground">{optionValue as string}</label>
                                  </div>
                                );
                              })}
                            </div>
                          ) : submissionValue && (submissionValue.field_type === 'file' || submissionValue.field_type === 'image_upload') ? (
                            <div className="w-full px-3 py-2 border border-border rounded-md bg-muted">
                              {value}
                            </div>
                          ) : (
                            <Input
                              type={submissionValue && submissionValue.field_type === 'email' ? 'email' : submissionValue && submissionValue.field_type === 'number' ? 'number' : submissionValue && submissionValue.field_type === 'date' ? 'date' : 'text'}
                              value={value}
                              disabled
                              className="bg-muted text-foreground"
                            />
                          )}
                        </div>
                      );
                    });
                  })()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p>Select a submission from the list to view its details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default FormSubmissions;